import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';

import { Message } from 'components/shared';
import LoadingSpinner from 'components/LoadingSpinner';
import { slugify } from 'utils/string-helpers';

import HoldingTable from './components';
import { HOLDING_INVESTOR_PERFORMANCE_GROUP_SHAPE } from './utils';

const HoldingInvestorPerformanceWidget = ({ state, widget, setTableExportData, forwardRef }) => {
  const { data: response, isLoading, error, isError } = state;
  const { overlay } = widget;
  const { widgetTitle } = overlay;

  useEffect(() => {
    if (!response || isLoading || isError) {
      return;
    }

    const addGroupToExportData = (array, group) => {
      const { holdingName, groupData, groupTotals } = group;
      array.push({ holdingName });
      groupData.forEach(g => array.push(g));
      if (groupTotals) {
        array.push({ ...groupTotals, portfolio: 'Total' });
      }
    };

    const exportData = response.holdingsData.reduce((acc, next) => {
      addGroupToExportData(acc, next);
      return acc;
    }, []);

    if (response.allInvestedData) {
      addGroupToExportData(exportData, response.allInvestedData);
    }

    setTableExportData({
      title: widgetTitle && slugify(widgetTitle),
      columns: [],
      rows: exportData
    });
  }, [response, isLoading, isError, setTableExportData, widgetTitle]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  const renderHoldingTables = () => {
    const { holdingsData } = response;
    return holdingsData.map(holding => {
      const { holdingName, groupData, groupTotals } = holding;
      return (
        <HoldingTable
          key={holdingName}
          holdingName={holdingName}
          groupData={groupData}
          groupTotals={groupTotals}
        />
      );
    });
  };

  const renderTotals = () => {
    const { allInvestedData } = response;
    if (allInvestedData === null) {
      return;
    }
    const { holdingName, groupData, groupTotals } = allInvestedData;

    // eslint-disable-next-line consistent-return
    return (
      <HoldingTable holdingName={holdingName} groupData={groupData} groupTotals={groupTotals} />
    );
  };

  return (
    <Box ref={forwardRef}>
      {renderHoldingTables()}
      {renderTotals()}
    </Box>
  );
};

HoldingInvestorPerformanceWidget.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      holdingsData: PropTypes.arrayOf(HOLDING_INVESTOR_PERFORMANCE_GROUP_SHAPE),
      allInvestedData: HOLDING_INVESTOR_PERFORMANCE_GROUP_SHAPE
    })
  }),
  widget: PropTypes.shape({
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string
    })
  }).isRequired,
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

HoldingInvestorPerformanceWidget.defaultProps = {
  state: {},
  setTableExportData: null,
  forwardRef: null
};

export default HoldingInvestorPerformanceWidget;
